import {FEATURE_FLAGS, MODULE_TYPES} from '../../../constants';
import {hasModule} from './ModuleUtil';

const CACHE = new Set();

const SESSION_STORAGE_KEY = 'availableFeatureFlags';

const isSessionStorageAvailable = () => typeof window !== 'undefined' && window.sessionStorage;

export type Feature = {key: string};
export const setAvailableFeatureFlags = (availableFeatureFlags: Feature[]): void => {
	if (Array.isArray(availableFeatureFlags)) {
		if (isSessionStorageAvailable()) {
			sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(availableFeatureFlags));
		}

		CACHE.clear();
		availableFeatureFlags.forEach(featureFlag => {
			CACHE.add(featureFlag.key);
		});
	}
};

export const hasFeatureFlag = (featureFlag: string): boolean => {
	if (CACHE.size === 0) {
		const sessionFeatureFlags = isSessionStorageAvailable() ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null;
		const availableFeatureFlags = sessionFeatureFlags ? JSON.parse(sessionFeatureFlags) : [];
		setAvailableFeatureFlags(availableFeatureFlags);
	}

	return CACHE.has(featureFlag);
};

export const hasTimeLocking = (): boolean => {
	return hasModule(MODULE_TYPES.TIME_LOCKING);
};

// The client-contacts feature (IGNITE-490/491/493/494) is gated by BOTH the
// `extending_client_contacts` feature flag and the NEW_CLIENT_CONTACTS_FEATURE module.
// Centralised here so the mandatory ≥2-week flag cleanup (AGENTS.md) is a single-site edit.
export const isClientContactsFeatureEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.EXTENDING_CLIENT_CONTACTS) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);

// The quotes stopgap is gated by BOTH the `quotes_builder` feature flag and the OPPORTUNITIES
// module (reused — quotes belong to opportunity projects, there is no separate quotes module).
// Centralised here so the mandatory ≥2-week flag cleanup is a single-site edit.
export const isQuotesEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.QUOTES_BUILDER) && hasModule(MODULE_TYPES.OPPORTUNITIES);

// Unit4 (T4-103) is gated on the UNIT4 module alone, per Niels: "these should be rolled into 1
// module". The `use_unit4` / `unit4_error_log` feature flags are gone -- UNIT4 is granted per
// company rather than by tier, so the module is the entitlement. This covers the Unit4 Error Log
// too: it is part of the Unit4 integration, not a separately licensed feature.
export const isUnit4Enabled = (): boolean => hasModule(MODULE_TYPES.UNIT4);

// Harvest (T4-103) moved off the `show_harvest_integration` feature flag onto the HARVEST module.
// HARVEST is granted per company and is deliberately not part of any tier, so the module is the
// whole entitlement -- see TierTest.noTierGrantsHarvestModule.
export const isHarvestEnabled = (): boolean => hasModule(MODULE_TYPES.HARVEST);

export const hasTimeApproval = (): boolean => {
	return hasModule(MODULE_TYPES.TIME_LOCKING);
};

export const usesSso = (company?: {oktaEnabled?: boolean; oneloginEnabled?: boolean; AADEnabled?: boolean}): boolean => {
	const {oktaEnabled, oneloginEnabled, AADEnabled} = company || {};
	return !!oktaEnabled || !!oneloginEnabled || !!AADEnabled;
};

export const usingTimeApproval = (companyUseTimeApproval: boolean): boolean => {
	return companyUseTimeApproval && hasTimeApproval();
};

export const usingInternalTimeApproval = (companyUseInternalTimeApproval: boolean): boolean => {
	return hasTimeApproval() && companyUseInternalTimeApproval;
};

export const usingTimeOffApproval = (companyUseTimeOffApproval: boolean): boolean => {
	return companyUseTimeOffApproval && hasTimeApproval();
};
